import { withPath } from "library/api/api";
import { deleteHref, getHref, postHref } from "library/api/httpMethods";

interface PostPaymentCommonData {
  amount: number;
  payment_type: string;
  currency: string;
}

interface PostPaymentNewDebitCardData extends PostPaymentCommonData {
  single_use_token: string;
  masked_card_number: string;
  save_instrument: boolean;
}

interface PostPaymentSavedDebitCardData extends PostPaymentCommonData {
  instrument_id: string;
}

export type PostPaymentData =
  | PostPaymentNewDebitCardData
  | PostPaymentSavedDebitCardData;

interface PostPaymentResponse {
  status_url: string;
}

export const postPayments = (url: string, data: PostPaymentData, key: string) =>
  postHref<PostPaymentResponse>(url, data, {
    "Idempotency-Key": key,
  });

export const postPaymentsWebview = (
  url: string,
  data: PostPaymentData,
  key: string,
  token: string,
) =>
  postHref<PostPaymentResponse>(
    url,
    data,
    {
      "Idempotency-Key": key,
      Authorization: token,
      "X-Session-ID": window.sessionStorage.getItem("sessionId"),
      "LFS-Request-Channel": "Latitude_Mobile_Application",
    },
    false,
  );

interface PaymentGatewayResponse {
  pg_status: string;
  pg_response_code: string;
  pg_response_description: string;
}

export type DeclineCode =
  | "ISSUER_DECLINED"
  | "PAYMENT_AMOUNT_TOO_LOW"
  | "PAYMENT_AMOUNT_TOO_HIGH"
  | "PAYMENT_AMOUNT_GREATER_THAN_CREDIT_BALANCE"
  | "INVALID_CARD_REGION"
  | "INVALID_CARD_SCHEME"
  | "CARD_PRODUCT_REGION_NOT_MATCH";

interface GetPaymentStatusPendingResponse {
  status: "PENDING";
}

interface GetPaymentStatusApprovedResponse extends PaymentGatewayResponse {
  status: "APPROVED";
  pg_receipt_number: string;
}

interface GetPaymentStatusGatewayDeclined extends PaymentGatewayResponse {
  status: "DECLINED";
  decline_code: DeclineCode;
}

interface GetPaymentStatusBusinessRulesDeclined {
  status: "BUSINESS_RULES_DECLINED";
  decline_code: DeclineCode;
}
interface GetPaymentStatusFailed extends PaymentGatewayResponse {
  status: "FAILED";
}

interface GetPaymentStatusFailedInternal {
  status: "FAILED_INTERNAL";
  error_message: string;
}

type GetPaymentStatusResponse =
  | GetPaymentStatusPendingResponse
  | GetPaymentStatusApprovedResponse
  | GetPaymentStatusBusinessRulesDeclined
  | GetPaymentStatusGatewayDeclined
  | GetPaymentStatusFailed
  | GetPaymentStatusFailedInternal;

export const getPaymentStatus = (url: string, key: string) =>
  getHref<GetPaymentStatusResponse>(
    url,
    {},
    {
      "Idempotency-Key": key,
    },
  );

export async function getPaymentStatusWebview(
  url: string,
  key: string,
  token: string,
) {
  return getHref<GetPaymentStatusResponse>(
    url,
    {},
    {
      "Idempotency-Key": key,
      Authorization: token,
      "X-Session-ID": window.sessionStorage.getItem("sessionId"),
    },
    false,
  );
}

export type CardScheme =
  | "VISA"
  | "MASTERCARD"
  | "AMEX"
  | "DINERS"
  | "JCB"
  | "UNIONPAY";

export interface PaymentInstrument {
  instrument_id: string;
  last_4_digits: string;
  card_scheme: CardScheme;
  card_region: "AU" | "NZ";
  expiry_date_month: string;
  expiry_date_year: string;
  card_type: string;
  last_used_at: string;
}

export interface GetPaymentInstrumentsResponse {
  data: PaymentInstrument[];
}

export const getPaymentInstruments = () =>
  getHref<GetPaymentInstrumentsResponse>(withPath(`v1/payments/instruments`));

export const deletePaymentInstrument = (id: string) =>
  deleteHref<undefined>(withPath(`v1/payments/instruments/${id}`));
